import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Dashboard from './pages/Dashboard';
import Optimize from './pages/Optimize';
import Shipments from './pages/Shipments';
import Carriers from './pages/Carriers';
import Analytics from './pages/Analytics';
import ShipmentDetail from './pages/ShipmentDetail';
import PublicPlan from './pages/PublicPlan';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Upgrade from './pages/Upgrade';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentResult';
import { AuthProvider, useAuth } from './context/AuthContext';
import TrialBanner from './components/TrialBanner';
import { Loader2 } from 'lucide-react';

const PAGE_TITLES = {
  dashboard: 'Freight Optimization Dashboard',
  optimize: 'Optimize Shipment',
  shipments: 'Shipments',
  'shipment-detail': 'Shipment Detail',
  carriers: 'Carrier Matching',
  analytics: 'Savings & Analytics',
  upgrade: 'Upgrade',
};

function FullScreenLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'var(--lf-bg)' }}
    >
      <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--lf-muted)' }}>
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    </div>
  );
}

function MainApp() {
  const [page, setPage] = React.useState('dashboard');
  const [detailId, setDetailId] = React.useState(null);
  const navigate = useNavigate();

  const openDetail = (id) => {
    setDetailId(id);
    setPage('shipment-detail');
  };

  const changePage = (next) => {
    if (next === 'upgrade') {
      navigate('/upgrade');
      return;
    }
    if (next !== 'shipment-detail') setDetailId(null);
    setPage(next);
  };

  const renderPage = () => {
    switch (page) {
      case 'optimize':
        return <Optimize />;
      case 'shipments':
        return <Shipments onOpen={openDetail} />;
      case 'shipment-detail':
        return (
          <ShipmentDetail shipmentId={detailId} onBack={() => changePage('shipments')} />
        );
      case 'carriers':
        return <Carriers />;
      case 'analytics':
        return <Analytics />;
      case 'dashboard':
      default:
        return <Dashboard onNavigate={changePage} />;
    }
  };

  return (
    <div className="App flex flex-col min-h-screen" style={{ background: 'var(--lf-bg)' }}>
      <TrialBanner />
      <div className="flex flex-1">
        <Sidebar current={page === 'shipment-detail' ? 'shipments' : page} onChange={changePage} />
        <main className="flex-1 p-6 md:p-8 max-w-[1500px]">
          <Topbar title={PAGE_TITLES[page]} />
          <div className="lf-fade-in" key={`${page}-${detailId || ''}`}>
            {renderPage()}
          </div>
        </main>
      </div>
    </div>
  );
}

function UpgradePage() {
  const navigate = useNavigate();
  return (
    <div className="App flex flex-col min-h-screen" style={{ background: 'var(--lf-bg)' }}>
      <TrialBanner />
      <div className="flex flex-1">
        <Sidebar current="dashboard" onChange={(p) => navigate(p === 'upgrade' ? '/upgrade' : '/')} />
        <main className="flex-1 p-6 md:p-8 max-w-[1500px]">
          <Topbar title="Upgrade" />
          <div className="lf-fade-in">
            <Upgrade onBack={() => navigate('/')} />
          </div>
        </main>
      </div>
    </div>
  );
}

function ProtectedRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Allow /share/plan/:id without auth
  if (location.pathname.startsWith('/share/plan/')) return null;

  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return null;
}

function AuthRouter() {
  const location = useLocation();

  // Handle auth callback hash BEFORE normal routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/share/plan/:id" element={<PublicPlan />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route
        path="/upgrade"
        element={
          <RequireAuth>
            <UpgradePage />
          </RequireAuth>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <MainApp />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
